import { useEffect, useState, useCallback } from 'react'
import { useAuthStore } from '@/stores/authStore'
import { useWebSocketStore } from '@/stores/websocketStore'
import {
  Clock, ChefHat, CheckCircle, XCircle, Timer, Printer,
  ChevronDown, ChevronUp, Bell, AlertCircle, Users, Package
} from 'lucide-react'
import { api } from '@/lib/api'
import { useElapsedTime } from '@/hooks/useElapsedTime'
import { toast } from 'sonner'
import { Button, Badge } from '@/components/ui'

interface OrderItem {
  id: string
  productId: string
  productName?: string
  product?: {
    name: string
    price: number
  }
  quantity: number
  unitPrice: number
  itemTotal: number
  customizations?: string
  notes?: string
}

interface Order {
  id: string
  tableSessionId: string
  customerId: string
  customerName?: string
  status: 'CART' | 'SUBMITTED' | 'PREPARING' | 'READY' | 'SERVED' | 'PAID' | 'CANCELLED'
  items: OrderItem[]
  subtotal: number
  tax: number
  total: number
  notes?: string
  createdAt: string
  updatedAt: string
  tableNumber?: string
}

type ViewMode = 'kanban' | 'list'

const statusConfig = {
  SUBMITTED: {
    label: 'Nuovo',
    variant: 'warning' as const,
    icon: Bell,
    bgColor: 'bg-orange-50 dark:bg-orange-500/10',
    borderColor: 'border-orange-300 dark:border-orange-500/30'
  },
  PREPARING: {
    label: 'In Preparazione',
    variant: 'info' as const,
    icon: ChefHat,
    bgColor: 'bg-blue-50 dark:bg-blue-500/10',
    borderColor: 'border-blue-300 dark:border-blue-500/30'
  },
  READY: {
    label: 'Pronto',
    variant: 'success' as const,
    icon: CheckCircle,
    bgColor: 'bg-emerald-50 dark:bg-emerald-500/10',
    borderColor: 'border-emerald-300 dark:border-emerald-500/30'
  },
  SERVED: {
    label: 'Servito',
    variant: 'neutral' as const,
    icon: CheckCircle,
    bgColor: 'bg-gray-50 dark:bg-white/[0.03]',
    borderColor: 'border-gray-200 dark:border-white/[0.08]'
  },
  PAID: {
    label: 'Pagato',
    variant: 'success' as const,
    icon: CheckCircle,
    bgColor: 'bg-violet-50 dark:bg-violet-500/10',
    borderColor: 'border-violet-300 dark:border-violet-500/30'
  },
  CANCELLED: {
    label: 'Annullato',
    variant: 'error' as const,
    icon: XCircle,
    bgColor: 'bg-red-50 dark:bg-red-500/10',
    borderColor: 'border-red-300 dark:border-red-500/30'
  },
}

// Component to show elapsed time with urgency colors
function OrderTimer({ createdAt }: { createdAt: string }) {
  const elapsed = useElapsedTime(createdAt)
  const diffMins = Math.floor((new Date().getTime() - new Date(createdAt).getTime()) / 60000)

  const getVariant = () => {
    if (diffMins > 30) return 'error' as const
    if (diffMins > 20) return 'warning' as const
    if (diffMins > 10) return 'warning' as const
    return 'info' as const
  }

  const getIcon = () => {
    if (diffMins > 30) return AlertCircle
    if (diffMins > 20) return AlertCircle
    return Timer
  }

  const TimerIcon = getIcon()

  return (
    <Badge variant={getVariant()} icon={TimerIcon} className={diffMins > 30 ? 'animate-pulse' : ''}>
      {elapsed}
    </Badge>
  )
}

// Expanded Order Card Component
function OrderCard({ order, onStatusUpdate, onPrint, updating }: {
  order: Order
  onStatusUpdate: (orderId: string, status: string) => void
  onPrint: (order: Order) => void
  updating: string | null
}) {
  const [expanded, setExpanded] = useState(false)
  const config = statusConfig[order.status as keyof typeof statusConfig]
  const StatusIcon = config?.icon

  const getNextStatus = (currentStatus: string) => {
    const statusFlow = {
      SUBMITTED: 'PREPARING',
      PREPARING: 'READY',
      READY: 'SERVED',
    }
    return statusFlow[currentStatus as keyof typeof statusFlow]
  }

  const getActionLabel = (currentStatus: string) => {
    const labels = {
      SUBMITTED: 'Inizia',
      PREPARING: 'Pronto',
      READY: 'Servito',
    }
    return labels[currentStatus as keyof typeof labels]
  }

  const nextStatus = getNextStatus(order.status)
  const actionLabel = getActionLabel(order.status)

  return (
    <div className={`bg-white dark:bg-[#25252d] rounded-2xl shadow-sm dark:shadow-none border ${config?.borderColor} overflow-hidden transition-all hover:shadow-md dark:hover:border-white/[0.15]`}>
      {/* Order Header - Always Visible */}
      <div className={`p-4 ${config?.bgColor} border-b ${config?.borderColor}`}>
        <div className="flex justify-between items-start mb-3">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              {order.tableNumber && (
                <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                  Tavolo {order.tableNumber}
                </h3>
              )}
              {order.customerName && (
                <span className="text-sm font-medium text-gray-600 dark:text-white/70 bg-gray-100 dark:bg-white/[0.06] px-2.5 py-1 rounded-lg">
                  {order.customerName}
                </span>
              )}
              {!order.tableNumber && !order.customerName && (
                <h3 className="text-lg font-bold text-gray-900 dark:text-white">Cliente</h3>
              )}
              <Badge variant={config?.variant} icon={StatusIcon}>
                {config?.label}
              </Badge>
            </div>
            <div className="flex items-center gap-2">
              <OrderTimer createdAt={order.createdAt} />
              <span className="text-xs text-gray-400 dark:text-white/30">#{order.id.slice(0, 8)}</span>
            </div>
          </div>
        </div>

        {/* Quick Summary */}
        <div className="flex items-center justify-between text-sm">
          <div className="flex items-center gap-4">
            <span className="flex items-center gap-1 text-gray-600 dark:text-white/60">
              <Package size={14} />
              <strong>{order.items.length}</strong> prodotti
            </span>
            <span className="text-2xl font-bold text-orange-500 dark:text-orange-400">
              €{order.total.toFixed(2)}
            </span>
          </div>
          <button
            onClick={() => setExpanded(!expanded)}
            className="p-1.5 hover:bg-gray-200 dark:hover:bg-white/[0.06] rounded-lg transition text-gray-500 dark:text-white/40"
          >
            {expanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
          </button>
        </div>
      </div>

      {/* Order Details - Expandable */}
      {expanded && (
        <div className="p-4 bg-gray-50 dark:bg-[#1e1e24]">
          {/* Items List */}
          <div className="space-y-2 mb-4">
            <h4 className="font-semibold text-sm text-gray-600 dark:text-white/60 mb-2">Prodotti:</h4>
            {order.items.map(item => (
              <div key={item.id} className="bg-white dark:bg-white/[0.04] p-3 rounded-xl border border-gray-200 dark:border-white/[0.06]">
                <div className="flex justify-between items-start mb-1">
                  <div className="flex-1">
                    <div className="flex items-start gap-2">
                      <span className="font-bold text-orange-500 dark:text-orange-400 min-w-[24px]">{item.quantity}x</span>
                      <div className="flex-1">
                        <p className="font-medium text-gray-900 dark:text-white">
                          {item.product?.name || item.productName || 'Prodotto'}
                        </p>
                        {item.customizations && (
                          <p className="text-xs text-orange-600 dark:text-orange-400 mt-1 font-medium">
                            🔸 {item.customizations}
                          </p>
                        )}
                        {item.notes && (
                          <p className="text-xs text-blue-600 dark:text-blue-400 mt-1 italic">
                            💬 {item.notes}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                  <span className="font-bold text-gray-900 dark:text-white ml-2">
                    €{item.itemTotal.toFixed(2)}
                  </span>
                </div>
              </div>
            ))}
          </div>

          {/* Order Notes */}
          {order.notes && (
            <div className="bg-orange-50 dark:bg-orange-500/10 border border-orange-200 dark:border-orange-500/20 rounded-xl p-3 mb-4">
              <p className="text-sm font-semibold text-orange-600 dark:text-orange-400 mb-1">📝 Note ordine:</p>
              <p className="text-sm text-gray-800 dark:text-white/80">{order.notes}</p>
            </div>
          )}

          {/* Total Breakdown */}
          <div className="border-t border-gray-200 dark:border-white/[0.06] pt-3 mb-4 space-y-1">
            <div className="flex justify-between text-sm text-gray-600 dark:text-white/60">
              <span>Subtotale</span>
              <span>€{order.subtotal.toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-sm text-gray-600 dark:text-white/60">
              <span>IVA</span>
              <span>€{order.tax.toFixed(2)}</span>
            </div>
            <div className="flex justify-between font-bold text-lg text-gray-900 dark:text-white pt-2 border-t border-gray-200 dark:border-white/[0.06]">
              <span>Totale</span>
              <span className="text-orange-500 dark:text-orange-400">€{order.total.toFixed(2)}</span>
            </div>
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="p-4 bg-gray-50 dark:bg-white/[0.02] border-t border-gray-200 dark:border-white/[0.06] flex gap-2">
        {nextStatus && (
          <Button
            onClick={() => onStatusUpdate(order.id, nextStatus)}
            disabled={updating === order.id}
            loading={updating === order.id}
            variant="primary"
            size="sm"
            className="flex-1"
          >
            ✓ {actionLabel}
          </Button>
        )}
        <Button
          onClick={() => onPrint(order)}
          variant="ghost"
          size="sm"
          className="flex items-center justify-center gap-2 px-4"
        >
          <Printer size={18} />
        </Button>
        {order.status === 'SUBMITTED' && (
          <Button
            onClick={() => onStatusUpdate(order.id, 'CANCELLED')}
            disabled={updating === order.id}
            variant="ghost"
            size="sm"
            className="px-4 text-red-500 hover:bg-red-500/10"
          >
            <XCircle size={18} />
          </Button>
        )}
      </div>
    </div>
  )
}

export default function OrdersPageImproved() {
  const { user } = useAuthStore()
  const { isConnected, subscribe } = useWebSocketStore()
  const [orders, setOrders] = useState<Order[]>([])
  const [loading, setLoading] = useState(true)
  const [filter, setFilter] = useState<'all' | 'active'>('active')
  const [viewMode, setViewMode] = useState<ViewMode>('kanban')
  const [updating, setUpdating] = useState<string | null>(null)

  // Play notification sound
  const playNotificationSound = () => {
    try {
      const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)()

      const beep = (frequency: number, duration: number, delay: number) => {
        setTimeout(() => {
          const oscillator = audioContext.createOscillator()
          const gainNode = audioContext.createGain()

          oscillator.connect(gainNode)
          gainNode.connect(audioContext.destination)

          oscillator.frequency.value = frequency
          oscillator.type = 'sine'

          gainNode.gain.setValueAtTime(0.3, audioContext.currentTime)
          gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + duration)

          oscillator.start(audioContext.currentTime)
          oscillator.stop(audioContext.currentTime + duration)
        }, delay)
      }

      beep(800, 0.15, 0)
      beep(1000, 0.15, 200)
      beep(1200, 0.2, 400)
    } catch (error) {
      console.error('Error playing notification sound:', error)
    }
  }

  const loadOrders = useCallback(async () => {
    try {
      const endpoint = filter === 'active' ? '/api/orders/active' : '/api/orders'
      console.log('🔄 Loading orders from:', endpoint)
      const response = await api.get(endpoint)
      console.log('✅ Loaded orders:', response.data.length)
      setOrders(response.data)
    } catch (error) {
      console.error('Error loading orders:', error)
      toast.error('Errore nel caricamento degli ordini')
    } finally {
      setLoading(false)
    }
  }, [filter])

  useEffect(() => {
    loadOrders()
    const interval = setInterval(loadOrders, 30000)
    return () => clearInterval(interval)
  }, [loadOrders])

  // WebSocket subscriptions
  useEffect(() => {
    if (!user?.restaurantId || !isConnected) return

    console.log('📡 WebSocket subscriptions active for restaurant:', user.restaurantId)

    const unsubscribeNew = subscribe(
      `/topic/restaurant/${user.restaurantId}/orders/new`,
      (order: Order) => {
        console.log('🆕 New order received:', order)
        setOrders((prev) => [order, ...prev])
        playNotificationSound()
        toast.success(`🔔 Nuovo ordine da ${order.customerName || order.tableNumber || 'Cliente'}!`, {
          description: `${order.items.length} prodotti - €${order.total.toFixed(2)}`,
          duration: 8000,
        })
      }
    )

    const unsubscribeStatus = subscribe(
      `/topic/restaurant/${user.restaurantId}/orders/status`,
      (order: Order) => {
        console.log('📊 Order status updated:', order)
        setOrders((prev) =>
          prev.map((o) => (o.id === order.id ? order : o))
        )
      }
    )

    return () => {
      unsubscribeNew()
      unsubscribeStatus()
    }
  }, [user?.restaurantId, isConnected, subscribe])

  const updateOrderStatus = async (orderId: string, newStatus: string) => {
    setUpdating(orderId)
    try {
      await api.patch(`/api/orders/${orderId}/status`, { status: newStatus })
      await loadOrders()
      toast.success('Stato ordine aggiornato')
    } catch (error) {
      console.error('Error updating order status:', error)
      toast.error('Errore durante l\'aggiornamento dello stato')
    } finally {
      setUpdating(null)
    }
  }

  const printReceipt = (order: Order) => {
    const printWindow = window.open('', '', 'width=300,height=600')
    if (!printWindow) {
      toast.error('Impossibile aprire la finestra di stampa')
      return
    }

    const receiptHTML = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Ricevuta Cucina</title>
          <style>
            body { font-family: monospace; font-size: 14px; margin: 10px; line-height: 1.4; }
            h1 { font-size: 20px; margin: 0 0 10px 0; font-weight: bold; }
            h2 { font-size: 16px; margin: 10px 0 5px 0; font-weight: bold; }
            .header { text-align: center; border-bottom: 2px solid #000; padding-bottom: 10px; margin-bottom: 10px; }
            .item { margin: 8px 0; padding: 5px 0; border-bottom: 1px dashed #999; }
            .item-name { font-weight: bold; font-size: 14px; }
            .item-qty { font-weight: bold; font-size: 16px; }
            .item-note { font-style: italic; margin-top: 3px; color: #666; }
            .item-custom { font-weight: bold; margin-top: 3px; }
            .total { border-top: 2px solid #000; padding-top: 10px; margin-top: 10px; font-weight: bold; font-size: 16px; }
            .time { font-size: 12px; color: #666; }
            .notes-section { margin-top: 10px; border-top: 1px solid #000; padding-top: 10px; }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>${user?.restaurantName || 'RISTORANTE'}</h1>
            <div class="time">${new Date(order.createdAt).toLocaleString('it-IT')}</div>
            <div style="font-size: 16px; font-weight: bold; margin-top: 5px;">
              ${order.tableNumber ? `TAVOLO ${order.tableNumber}` : order.customerName || 'Cliente'}
            </div>
            <div style="font-size: 12px;">Ordine #${order.id.slice(0, 8)}</div>
          </div>
          <h2>═══ ARTICOLI ═══</h2>
          ${order.items.map(item => `
            <div class="item">
              <div style="display: flex; justify-content: space-between; align-items: center;">
                <span class="item-qty">${item.quantity}x</span>
                <span class="item-name" style="flex: 1; margin-left: 10px;">${item.product?.name || item.productName || 'Prodotto'}</span>
              </div>
              ${item.customizations ? `<div class="item-custom">▸ ${item.customizations}</div>` : ''}
              ${item.notes ? `<div class="item-note">⚠ ${item.notes}</div>` : ''}
            </div>
          `).join('')}
          ${order.notes ? `
            <div class="notes-section">
              <h2>📝 NOTE ORDINE:</h2>
              <div style="font-weight: bold; margin-top: 5px;">${order.notes}</div>
            </div>
          ` : ''}
          <div class="total">
            <div style="display: flex; justify-content: space-between;">
              <span>TOTALE:</span>
              <span>€ ${order.total.toFixed(2)}</span>
            </div>
          </div>
        </body>
      </html>
    `

    printWindow.document.write(receiptHTML)
    printWindow.document.close()
    printWindow.focus()
    setTimeout(() => {
      printWindow.print()
      printWindow.close()
    }, 250)

    toast.success('Ricevuta stampata')
  }

  const activeOrders = orders.filter(o =>
    ['SUBMITTED', 'PREPARING', 'READY'].includes(o.status)
  )

  // Get displayed orders based on current filter
  const displayedOrders = filter === 'active' ? activeOrders : orders

  const getOrdersByStatus = (status: string) => {
    return displayedOrders.filter(o => o.status === status)
  }

  if (!user?.restaurantId) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-gray-500 dark:text-slate-400">Nessun ristorante associato</p>
      </div>
    )
  }

  return (
    <div className="p-4 md:p-6 lg:p-8">
      {/* Header */}
      <div className="mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white">Gestione Ordini</h1>
            <p className="text-gray-500 dark:text-white/40 text-sm mt-1">Visualizza e gestisci gli ordini in tempo reale</p>
          </div>
          <div className="flex items-center gap-3">
            {/* View Mode Toggle */}
            <div className="flex bg-white dark:bg-[#25252d] rounded-xl p-1 border border-gray-200 dark:border-white/[0.08]">
              <button
                onClick={() => setViewMode('kanban')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
                  viewMode === 'kanban'
                    ? 'bg-orange-500 text-white shadow-sm'
                    : 'text-gray-600 dark:text-white/60 hover:text-gray-900 dark:hover:text-white'
                }`}
              >
                Kanban
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
                  viewMode === 'list'
                    ? 'bg-orange-500 text-white shadow-sm'
                    : 'text-gray-600 dark:text-white/60 hover:text-gray-900 dark:hover:text-white'
                }`}
              >
                Lista
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Filter Tabs */}
      <div className="mb-6 bg-white dark:bg-[#25252d] rounded-xl p-1.5 border border-gray-200 dark:border-white/[0.08]">
        <nav className="flex gap-2">
          <button
            onClick={() => setFilter('active')}
            className={`flex-1 py-2.5 px-4 rounded-lg font-medium text-sm transition ${
              filter === 'active'
                ? 'bg-orange-500 text-white shadow-sm'
                : 'text-gray-600 dark:text-white/60 hover:bg-gray-100 dark:hover:bg-white/[0.04]'
            }`}
          >
            Ordini Attivi
            {activeOrders.length > 0 && (
              <span className={`ml-2 rounded-full px-2 py-0.5 text-xs font-bold ${
                filter === 'active' ? 'bg-white/20 text-white' : 'bg-orange-500 text-white'
              }`}>
                {activeOrders.length}
              </span>
            )}
          </button>
          <button
            onClick={() => setFilter('all')}
            className={`flex-1 py-2.5 px-4 rounded-lg font-medium text-sm transition ${
              filter === 'all'
                ? 'bg-orange-500 text-white shadow-sm'
                : 'text-gray-600 dark:text-white/60 hover:bg-gray-100 dark:hover:bg-white/[0.04]'
            }`}
          >
            Tutti gli Ordini
            <span className={`ml-2 rounded-full px-2 py-0.5 text-xs font-bold ${
              filter === 'all' ? 'bg-white/20 text-white' : 'bg-gray-200 dark:bg-white/[0.1] text-gray-600 dark:text-white/60'
            }`}>
              {orders.length}
            </span>
          </button>
        </nav>
      </div>

      {/* Orders Display */}
      {loading ? (
        <div className="text-center py-20">
          <div className="inline-block animate-spin rounded-full h-10 w-10 border-2 border-orange-500 border-t-transparent"></div>
          <p className="text-gray-500 dark:text-white/40 mt-4">Caricamento ordini...</p>
        </div>
      ) : displayedOrders.length === 0 ? (
        <div className="text-center py-20 bg-white dark:bg-[#25252d] rounded-2xl border border-gray-200 dark:border-white/[0.08]">
          <div className="text-6xl mb-4">📦</div>
          <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
            Nessun ordine {filter === 'active' ? 'attivo' : ''}
          </h3>
          <p className="text-gray-500 dark:text-white/40">
            {filter === 'active'
              ? 'Gli ordini appariranno qui quando i clienti li invieranno'
              : 'Non ci sono ancora ordini nel sistema'}
          </p>
        </div>
      ) : viewMode === 'kanban' ? (
        /* Kanban View */
        filter === 'active' ? (
          /* Active Orders: 3 columns */
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Nuovi */}
            <div className="bg-orange-50 dark:bg-orange-500/5 rounded-2xl p-4 border border-orange-200 dark:border-orange-500/20">
              <h3 className="font-bold text-base mb-4 flex items-center gap-2 text-orange-600 dark:text-orange-400">
                <Bell size={18} />
                Nuovi
                <span className="ml-auto bg-orange-500 text-white rounded-full px-2.5 py-0.5 text-xs font-semibold">
                  {getOrdersByStatus('SUBMITTED').length}
                </span>
              </h3>
              <div className="space-y-3">
                {getOrdersByStatus('SUBMITTED').map(order => (
                  <OrderCard
                    key={order.id}
                    order={order}
                    onStatusUpdate={updateOrderStatus}
                    onPrint={printReceipt}
                    updating={updating}
                  />
                ))}
              </div>
            </div>

            {/* In Preparazione */}
            <div className="bg-blue-50 dark:bg-blue-500/5 rounded-2xl p-4 border border-blue-200 dark:border-blue-500/20">
              <h3 className="font-bold text-base mb-4 flex items-center gap-2 text-blue-600 dark:text-blue-400">
                <ChefHat size={18} />
                In Preparazione
                <span className="ml-auto bg-blue-500 text-white rounded-full px-2.5 py-0.5 text-xs font-semibold">
                  {getOrdersByStatus('PREPARING').length}
                </span>
              </h3>
              <div className="space-y-3">
                {getOrdersByStatus('PREPARING').map(order => (
                  <OrderCard
                    key={order.id}
                    order={order}
                    onStatusUpdate={updateOrderStatus}
                    onPrint={printReceipt}
                    updating={updating}
                  />
                ))}
              </div>
            </div>

            {/* Pronti */}
            <div className="bg-emerald-50 dark:bg-emerald-500/5 rounded-2xl p-4 border border-emerald-200 dark:border-emerald-500/20">
              <h3 className="font-bold text-base mb-4 flex items-center gap-2 text-emerald-600 dark:text-emerald-400">
                <CheckCircle size={18} />
                Pronti
                <span className="ml-auto bg-emerald-500 text-white rounded-full px-2.5 py-0.5 text-xs font-semibold">
                  {getOrdersByStatus('READY').length}
                </span>
              </h3>
              <div className="space-y-3">
                {getOrdersByStatus('READY').map(order => (
                  <OrderCard
                    key={order.id}
                    order={order}
                    onStatusUpdate={updateOrderStatus}
                    onPrint={printReceipt}
                    updating={updating}
                  />
                ))}
              </div>
            </div>
          </div>
        ) : (
          /* All Orders: 6 columns (2 rows) */
          <div className="space-y-8">
            {/* First Row: Active Orders */}
            <div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Ordini Attivi</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Nuovi */}
                <div className="bg-orange-50 dark:bg-orange-500/5 rounded-2xl p-4 border border-orange-200 dark:border-orange-500/20">
                  <h3 className="font-bold text-sm mb-3 flex items-center gap-2 text-orange-600 dark:text-orange-400">
                    <Bell size={16} />
                    Nuovi
                    <span className="ml-auto bg-orange-500 text-white rounded-full px-2 py-0.5 text-xs font-semibold">
                      {getOrdersByStatus('SUBMITTED').length}
                    </span>
                  </h3>
                  <div className="space-y-3 max-h-[400px] overflow-y-auto">
                    {getOrdersByStatus('SUBMITTED').map(order => (
                      <OrderCard
                        key={order.id}
                        order={order}
                        onStatusUpdate={updateOrderStatus}
                        onPrint={printReceipt}
                        updating={updating}
                      />
                    ))}
                  </div>
                </div>

                {/* In Preparazione */}
                <div className="bg-blue-50 dark:bg-blue-500/5 rounded-2xl p-4 border border-blue-200 dark:border-blue-500/20">
                  <h3 className="font-bold text-sm mb-3 flex items-center gap-2 text-blue-600 dark:text-blue-400">
                    <ChefHat size={16} />
                    In Preparazione
                    <span className="ml-auto bg-blue-500 text-white rounded-full px-2 py-0.5 text-xs font-semibold">
                      {getOrdersByStatus('PREPARING').length}
                    </span>
                  </h3>
                  <div className="space-y-3 max-h-[400px] overflow-y-auto">
                    {getOrdersByStatus('PREPARING').map(order => (
                      <OrderCard
                        key={order.id}
                        order={order}
                        onStatusUpdate={updateOrderStatus}
                        onPrint={printReceipt}
                        updating={updating}
                      />
                    ))}
                  </div>
                </div>

                {/* Pronti */}
                <div className="bg-emerald-50 dark:bg-emerald-500/5 rounded-2xl p-4 border border-emerald-200 dark:border-emerald-500/20">
                  <h3 className="font-bold text-sm mb-3 flex items-center gap-2 text-emerald-600 dark:text-emerald-400">
                    <CheckCircle size={16} />
                    Pronti
                    <span className="ml-auto bg-emerald-500 text-white rounded-full px-2 py-0.5 text-xs font-semibold">
                      {getOrdersByStatus('READY').length}
                    </span>
                  </h3>
                  <div className="space-y-3 max-h-[400px] overflow-y-auto">
                    {getOrdersByStatus('READY').map(order => (
                      <OrderCard
                        key={order.id}
                        order={order}
                        onStatusUpdate={updateOrderStatus}
                        onPrint={printReceipt}
                        updating={updating}
                      />
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Second Row: Completed Orders */}
            <div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Ordini Completati</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Serviti */}
                <div className="bg-gray-50 dark:bg-white/[0.02] rounded-2xl p-4 border border-gray-200 dark:border-white/[0.06]">
                  <h3 className="font-bold text-sm mb-3 flex items-center gap-2 text-gray-600 dark:text-white/60">
                    <CheckCircle size={16} />
                    Serviti
                    <span className="ml-auto bg-gray-400 dark:bg-white/20 text-white rounded-full px-2 py-0.5 text-xs font-semibold">
                      {getOrdersByStatus('SERVED').length}
                    </span>
                  </h3>
                  <div className="space-y-3 max-h-[400px] overflow-y-auto">
                    {getOrdersByStatus('SERVED').map(order => (
                      <OrderCard
                        key={order.id}
                        order={order}
                        onStatusUpdate={updateOrderStatus}
                        onPrint={printReceipt}
                        updating={updating}
                      />
                    ))}
                  </div>
                </div>

                {/* Pagati */}
                <div className="bg-violet-50 dark:bg-violet-500/5 rounded-2xl p-4 border border-violet-200 dark:border-violet-500/20">
                  <h3 className="font-bold text-sm mb-3 flex items-center gap-2 text-violet-600 dark:text-violet-400">
                    <CheckCircle size={16} />
                    Pagati
                    <span className="ml-auto bg-violet-500 text-white rounded-full px-2 py-0.5 text-xs font-semibold">
                      {getOrdersByStatus('PAID').length}
                    </span>
                  </h3>
                  <div className="space-y-3 max-h-[400px] overflow-y-auto">
                    {getOrdersByStatus('PAID').map(order => (
                      <OrderCard
                        key={order.id}
                        order={order}
                        onStatusUpdate={updateOrderStatus}
                        onPrint={printReceipt}
                        updating={updating}
                      />
                    ))}
                  </div>
                </div>

                {/* Annullati */}
                <div className="bg-red-50 dark:bg-red-500/5 rounded-2xl p-4 border border-red-200 dark:border-red-500/20">
                  <h3 className="font-bold text-sm mb-3 flex items-center gap-2 text-red-600 dark:text-red-400">
                    <XCircle size={16} />
                    Annullati
                    <span className="ml-auto bg-red-500 text-white rounded-full px-2 py-0.5 text-xs font-semibold">
                      {getOrdersByStatus('CANCELLED').length}
                    </span>
                  </h3>
                  <div className="space-y-3 max-h-[400px] overflow-y-auto">
                    {getOrdersByStatus('CANCELLED').map(order => (
                      <OrderCard
                        key={order.id}
                        order={order}
                        onStatusUpdate={updateOrderStatus}
                        onPrint={printReceipt}
                        updating={updating}
                      />
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )
      ) : (
        /* List View */
        <div className="space-y-4">
          {displayedOrders.map(order => (
            <OrderCard
              key={order.id}
              order={order}
              onStatusUpdate={updateOrderStatus}
              onPrint={printReceipt}
              updating={updating}
            />
          ))}
        </div>
      )}
    </div>
  )
}
